import { useState } from 'react';
import { Auth } from "../../../../Common/lib/auth";

export default function PasswordSection({ userProfile }) {
  const [passwordEdit, setPasswordEdit] = useState(false);
  const [userPassword, setUserPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");


  /**
     * Handles password input field changes.
     * @param {*} field Name of userPassword property to change
     * @param {*} value Value from input field
     */
  function handlePasswordChange(field, value) {
    setUserPassword((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  }

  /**
   * Validates password fields and updates the user password.
   */
  async function updatePassword() {
    let isValid = true;
    setPasswordError("");

    // Check if any of the fields are empty
    if (userPassword.currentPassword === "" || userPassword.newPassword === "" || userPassword.confirmNewPassword === "") {
      setPasswordError("All fields are required");
      return false;
    }

    // Check if current password is the same as new password
    if (userPassword.currentPassword === userPassword.newPassword) {
      setPasswordError("New password cannot be the same as your current password");
      return false;
    }

    // Check that the password is at least 8 characters in length
    if (userPassword.newPassword.length < 8) {
      setPasswordError("Must be at least 8 characters long",);
      return false;
    }

    // Check that the password doesn't start or end with a space character (Cognito doesn't allow it)
    if (!(/^[\S]+.*[\S]+$/).test(userPassword.newPassword)) {
      setPasswordError("Passwords cannot start or end with a space");
      isValid = false;
    }

    // Check that the two passwords match each other
    if (userPassword.confirmNewPassword !== userPassword.newPassword) {
      setPasswordError("New passwords must match");
      isValid = false;
    }

    // Check if validation passed
    if (isValid) {
      try {
        // Update user password
        const user = await Auth.currentAuthenticatedUser();
        await Auth.changePassword(user, userPassword.currentPassword, userPassword.newPassword);

        // Clear errors and password variables
        setUserPassword({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        })
        setPasswordEdit(false);
        setPasswordError("");
      }
      catch (error) {
        setPasswordError("Incorrect current password");
      }
    }
  }

  /**
   * Cancels password editing mode and clears error messages.
   */
  async function cancelUpdatePassword() {
    setPasswordEdit(false);
    setPasswordError("");
  }

  return (
    <div className="bg-gray-50 border border-gray-200 shadow-sm rounded-xl p-8 dark:bg-gray-800 dark:border-gray-700">
      <div className="flex justify-between items-center">
        <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">Password</h2>
        {passwordEdit ? (
          <div className="flex">
            <button
              onClick={() => updatePassword()}
              type="button"
              className="rounded-md w-full bg-blue-600 py-1 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              data-cy="save-password"
            >
              Update
            </button>
            <button
              onClick={() => cancelUpdatePassword()}
              type="button"
              className="ml-2 rounded-md w-full py-1 px-4 text-sm font-medium shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:border dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
              onClick={() => setPasswordEdit(true)}
              type="button" className="font-semibold text-sm text-blue-600 hover:text-blue-500"
              data-cy="edit-password"
            >
            Edit
          </button>
        )}
      </div>

      <dl className="mt-2 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 dark:divide-gray-700 dark:border-gray-700">
        {passwordEdit ? (
          <>
            <div className="pt-6 sm:flex items-center">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">Current Password</dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <input
                  onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                  data-cy="current-password"
                  type="password"
                  className="max-w-md outline-none border border-gray-300 text-sm sm:text-base py-2 px-4 block w-full shadow-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </dd>
            </div>

            <div className="pt-6 sm:flex items-center">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">New Password</dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <input
                  onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                  data-cy="new-password"
                  type="password"
                  className="max-w-md outline-none border border-gray-300 text-sm sm:text-base py-2 px-4 block w-full shadow-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </dd>
            </div>

            <div className="pt-6 sm:flex items-center">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">Confirm Password</dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <input
                  onChange={(e) => handlePasswordChange("confirmNewPassword", e.target.value)}
                  data-cy="confirm-password"
                  type="password"
                  className="max-w-md outline-none border border-gray-300 text-sm sm:text-base py-2 px-4 block w-full shadow-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </dd>
            </div>
          </>
        ) : (
          <div className="pt-6 sm:flex items-center">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">Password</dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
              <div className="text-gray-900 dark:text-white">********</div> {/* no, we're not showing the user's actual password */}
            </dd>
          </div>
        )}
        {passwordError.length !== 0 &&
          <p className="italic text-sm block text-red-600">{passwordError}</p>
        }
      </dl>
    </div>
  );
}